import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import {Class} from '../../../generated/types';
import {cls, humanJoin} from '../../utils';
import {columnTitle, featureTitle, levelFeatures, SPELL_LEVELS_REGEXP} from '../columns';
import {Link} from '../../../lib/routes';
import {PartialFeature} from '../../feature-view';

import css from './grid-view.module.css';
import gridCss from '../../grid.module.css';

type Props = {
	heroClass: Class;
	features: PartialFeature[];
};


export const GridView: React.FC<Props> = ({heroClass, features}) => {
	const spellLevelsIndex = useMemo(() => {
		return heroClass.columns.findIndex((c) => c === '@1');
	}, [heroClass]);

	const hasSpellLevels = spellLevelsIndex > -1;

	const columnsSizes = useMemo(() => {
		return heroClass.columns.map((column) => {
			if (SPELL_LEVELS_REGEXP.test(column)) return '40px';
			else if (column === '@Level') return '55px';
			else if (column === '@ProficiencyBonus') return '65px';
			else if (column === '@PreparedSpells') return '60px';
			else if (column === '@Features') return 'minmax(200px, 1fr)';
			else return '100px';
		});
	}, [heroClass]);

	const headersRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const handleScroll = useCallback(() => {
		headersRef.current!.scrollLeft = contentRef.current!.scrollLeft!;
	}, []);

	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function stop(e: TouchEvent) {
			e.stopPropagation();
		}
		wrapperRef.current?.addEventListener('touchstart', stop);
		wrapperRef.current?.addEventListener('touchcancel', stop);
		wrapperRef.current?.addEventListener('touchmove', stop);
		wrapperRef.current?.addEventListener('touchend', stop);

		return () => {
			wrapperRef.current?.removeEventListener('touchstart', stop);
			wrapperRef.current?.removeEventListener('touchcancel', stop);
			wrapperRef.current?.removeEventListener('touchmove', stop);
			wrapperRef.current?.removeEventListener('touchend', stop);
		};
	}, []);

	return (
		<div className={css.wrapper} ref={wrapperRef}>
			<div className={css.headersWrapper}>
				<div
					className={cls(gridCss.grid, css.grid)}
					role="grid"
					style={{
						gridTemplateColumns: columnsSizes.join(' '),
					}}
					ref={headersRef}
				>
					<div className={cls(gridCss.headers)} role="row" aria-rowindex={1}>
						{heroClass.columns.map((column, index) => {
							const columnProps = column;
							if (columnTitle(column) !== column) column = columnTitle(column);
							else if (SPELL_LEVELS_REGEXP.test(column)) column = column.replace('@', '');
							return (
								<React.Fragment key={column + index}>
									<div
										key={column}
										className={cls(gridCss.column, columnProps === '@Level' && css.level)}
										style={
											hasSpellLevels && index < spellLevelsIndex ? {gridRow: 'span 2'} : undefined
										}
									>
										<div
											className={cls(
												gridCss.header,
												css.header,
												columnProps === '@Features' && css.leftJustify,
												columnProps === '@Features' && css.features,
											)}
										>
											{column}
										</div>
									</div>
									{hasSpellLevels && index + 1 === spellLevelsIndex && (
										<div
											className={cls(css.spellColspan, gridCss.column)}
											role={'gridcell'}
											style={{
												gridColumn: `span ${heroClass.columns.length - spellLevelsIndex}`,
											}}
										>
											<div className={cls(gridCss.header, gridCss.headerLine)}>
												Ячеек на круг заклинания
											</div>
										</div>
									)}
								</React.Fragment>
							);
						})}
					</div>
				</div>
			</div>
			<div
				className={cls(gridCss.grid, css.grid)}
				ref={contentRef}
				role="grid"
				onScroll={handleScroll}
				style={{
					gridTemplateColumns: columnsSizes.join(' '),
				}}
			>
				<div className={gridCss.body}>
					{heroClass.rows?.map((columns, index) => {
						return (
							<div className={gridCss.row} key={index} role="row" aria-rowindex={index + 2}>
								{heroClass.columns.map((column, columnIndex) => {
									let ColumnText: React.ReactNode = '';
									const level = (index + 1).toString();

									if (column === '@Features') {
										ColumnText = (
											<>
												{humanJoin(
													levelFeatures(features, level).map((feature) => (
														<Link
															route={'classFeature'}
															params={{id: heroClass.id, featureId: feature.id}}
															key={feature.id}
														>
															{featureTitle(feature, level)}
														</Link>
													)),
												)}
											</>
										);
									} else {
										ColumnText = (columns?.[columnIndex] && columns[columnIndex]) || '';
									}

									if (!ColumnText) ColumnText = '—';

									return (
										<div
											className={cls(
												gridCss.cell,
												css.cell,
												column === '@Features' && css.leftJustify,
												column === '@Features' && css.features,
												column === '@Level' && css.level,
											)}
											role="gridcell"
											aria-readonly="true"
											key={column}
										>
											{ColumnText}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};
